import "./voiceplayer.css";

import { Icon, IconVariants, Text } from "@next-platform/client-common-spicy-ui";
import { SampleFileFailureReason, useTranslation } from "@next-platform/client-common-core";
import { TFunction } from "i18next";
import React, { useCallback } from "react";

import { VoiceClipPlaceholder } from "@/pages/Voice/types";
import { cn } from "@/utils";

interface IVoiceClipProps {
  handleDeleteClick(name: string): void;
  audioFile: VoiceClipPlaceholder;
}

// eslint-disable-next-line id-length -- keep "t" as the standard i18n translation helper for readability and tooling support
const getClipErrorMessage = (failureReason: SampleFileFailureReason | undefined, t: TFunction): string | null => {
  if (!failureReason) return null;
  const clipErrorMessage = {
    [SampleFileFailureReason.EMPTY_FILE]: t("voice:form.error.emptyFile"),
    [SampleFileFailureReason.FILE_TOO_LARGE]: t("voice:form.error.fileTooLarge"),
    [SampleFileFailureReason.INVALID_CONTENT_TYPE]: t("voice:form.error.invalidContentType"),
    [SampleFileFailureReason.INVALID_FORMAT]: t("voice:form.error.invalidFormat"),
    [SampleFileFailureReason.INVALID_AUDIO_QUALITY]: t("voice:form.error.invalidAudioQuality"),
    [SampleFileFailureReason.INSUFFICIENT_AUDIO]: t("voice:form.error.insufficientAudio"),
    [SampleFileFailureReason.UNKNOWN]: t("voice:form.error.validationFailed"),
  };
  const message = clipErrorMessage[failureReason] ?? clipErrorMessage[SampleFileFailureReason.UNKNOWN];
  return message;
};

const VoiceClipPlayerPlaceholder = ({ handleDeleteClick, audioFile }: IVoiceClipProps) => {
  const { t } = useTranslation();
  const memoizedDeleterFunction = useCallback((string: string) => () => handleDeleteClick(string), [handleDeleteClick]);
  const errorMessage = getClipErrorMessage(audioFile.failure_reason, t);

  return (
    <div className="p-2 border border-solid border-gray-9 rounded-md ">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div
            className={cn("bg-black shrink-0 dark:!bg-white !border-transparent size-9 rounded-full grid place-content-center", "cursor-not-allowed")}
          >
            <Icon icon={IconVariants.PLAY} className="fill-white dark:fill-black dark:stroke-black" />
          </div>
          <div className="flex flex-col overflow-hidden">
            <Text className="opacity-80 text-sm leading-6 text-nowrap text-ellipsis overflow-hidden">{audioFile.filename}</Text>
            {!!errorMessage && <Text className="text-xs text-red-9">{errorMessage}</Text>}
          </div>
        </div>
        <div className="ml-3 cursor-pointer" onClick={memoizedDeleterFunction(audioFile.filename)}>
          <Icon icon={IconVariants.TRASH} className="stroke-gray-10" />
        </div>
      </div>
    </div>
  );
};
export default VoiceClipPlayerPlaceholder;
